import React from 'react';
import { urlSegura } from '../../../utils/urlSegura';
import { REDES } from '../../../utils/redesSociales';

/**
 * La barra de bienvenida del catálogo de genética, en sus DOS variantes.
 *
 * Existe un solo componente y no dos porque el CONTENIDO es el mismo en las
 * dos plantillas —mensaje, link, colores y redes salen de la misma
 * config— y lo único que cambia es la FORMA:
 *
 * - `variante="franja"` (OP-1): una tira FINA que va DENTRO de la cabecera
 *   fija, como primer hijo de `<header className="site-header">` —ver la
 *   prop `franjaSuperior` de `SiteHeader` en `RematePage.jsx`—. Chica a
 *   propósito: comparte lugar con el menú y no puede competir con él. Antes
 *   de esto la "barra de OP-1" se dibujaba en el FLUJO normal, antes del
 *   hero, y la cabecera es `position: fixed; zIndex: 1000`: quedaba tapada a
 *   y=0 detrás de ella y nunca se llegó a ver, en ningún catálogo.
 * - `variante="banda"` (OP-2): la banda de siempre —más alta, debajo del
 *   hero— y sólo en la portada.
 *
 * `null` sin `mensaje_bienvenida`: no hay switch de on/off aparte, mismo
 * criterio que el resto del micrositio — un interruptor que convive con el
 * contenido crea un segundo estado que se desincroniza.
 *
 * TEXTO a la izquierda, REDES (`config.redes`) a la derecha —lista
 * compartida con `ContactoPage.jsx`, en `utils/redesSociales.jsx`—. Cuando
 * hay `bienvenida_link`, el link envuelve SÓLO el texto: los íconos van cada
 * uno en su propio `<a>`, y un `<a>` adentro de otro `<a>` es HTML inválido.
 */
const PADDING_POR_VARIANTE = {
  banda: '12px 24px',
};

// ¿Es claro este color? Luminancia relativa aproximada de un hex de 3 o 6
// dígitos; cualquier otra cosa (`rgb(...)`, un nombre) se trata como claro,
// que es el default de la barra. Sólo decide de qué lado va el brillo.
const esClaro = (hex) => {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return true;
  const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 0.5;
};

// UN SOLO `<style>` por render de este componente —se dibuja UNA vez por
// página, no es una tarjeta repetida por dato como `AnimalCard`, así que no
// hay el riesgo de nodos duplicados que motiva insertar las hojas de estilo
// una sola vez en `CatalogoPage`—. Antes se armaba sólo si había link, porque
// lo único que llevaba era el hover. Ahora además lleva el breakpoint de la
// banda, así que se renderiza siempre, haya o no link.
const BIENVENIDA_CSS = `
/* El subrayado del hover lleva el color del texto a mano: el brillo de abajo
   deja el color real en transparent (background-clip: text), y sin esto la
   linea del underline seria invisible. */
.pj-bienvenida-link:hover { text-decoration: underline; text-decoration-color: var(--pjb-texto); }

/* EL BRILLO. Un destello suave recorre el mensaje de izquierda a derecha en
   casi tres segundos y descansa hasta completar los 6: la barra llama la
   atencion una vez por ciclo y el resto del tiempo es una barra quieta.
   (Arranco en 1,3 s y el usuario lo pidio mas lento: 2,7 s.)
   Mecanica: el texto se pinta con un degradado (background-clip: text) del
   color del texto con una franja de brillo en el medio, tres veces mas ancho
   que la caja, y lo que se anima es la POSICION del degradado. Los colores
   viajan en variables porque los elige el productor. */
.pj-bienvenida-texto {
  background-image: linear-gradient(
    100deg,
    var(--pjb-texto) 0%, var(--pjb-texto) 44%,
    var(--pjb-brillo) 50%,
    var(--pjb-texto) 56%, var(--pjb-texto) 100%
  );
  background-size: 250% 100%;
  background-position: 100% 0;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  animation: pj-bienvenida-brillo 6s ease-in-out infinite;
}
@keyframes pj-bienvenida-brillo {
  0%   { background-position: 100% 0; }
  45%  { background-position: 0% 0; }
  100% { background-position: 0% 0; }
}
/* Quien pidio menos movimiento no recibe destellos: texto plano y quieto. */
@media (prefers-reduced-motion: reduce) {
  .pj-bienvenida-texto {
    animation: none;
    background-image: none;
    color: var(--pjb-texto);
    -webkit-text-fill-color: var(--pjb-texto);
  }
}

/* Banda (OP-2): dos bloques uno al lado del otro en desktop, como el
   wireframe. Por debajo de 600px se apilan: el bloque de iconos pasa a
   ocupar el ancho completo y centra su contenido. */
@media (max-width: 600px) {
  .pj-bienvenida-banda-iconos {
    width: 100%;
    justify-content: center;
  }
}
`;

const BarraBienvenida = ({ config, tipografia, variante = 'banda' }) => {
  const mensaje = (config.mensaje_bienvenida || '').trim();
  if (!mensaje) return null;

  // Vacío = usa la identidad del productor: el color primario del catálogo y
  // texto blanco. Mismo criterio que el resto de la config —"Usar los de la
  // marca" en el panel vacía estas dos claves en vez de escribir el color
  // resuelto, así que si el productor cambia `color_primary` después, la
  // barra lo sigue sin que nadie vuelva a tocar nada.
  const fondo = config.bienvenida_color || config.color_primary || '#1a5f2a';
  const colorTexto = config.bienvenida_color_texto || '#fff';

  // LA MISMA LISTA BLANCA que el resto del micrositio: `bienvenida_link` es
  // texto libre que el productor carga en el panel y `set_config()` no
  // valida valores, sólo claves. Con un link inválido la barra se dibuja
  // igual, sin link — ver `urlSegura`.
  const destino = urlSegura(config.bienvenida_link);
  // Externa = tiene esquema (`http(s):`); interna es `/`, `#` o `./`, y esas
  // navegan sin abrir pestaña nueva.
  const externa = Boolean(destino) && /^https?:/i.test(destino);

  // Sólo las redes con VALOR cargado y con un `href` que resolvió: `urlDeRed`
  // devuelve `null` ante un valor que no tiene sentido (ver
  // `utils/urlSegura.js`), y ahí tampoco se dibuja el ícono — un link roto es
  // peor que no tenerlo, mismo criterio que el resto del micrositio.
  const redesActivas = REDES
    .filter((r) => config.redes?.[r.key])
    .map((r) => ({ ...r, url: r.href(config.redes[r.key]) }))
    .filter((r) => r.url);

  // El color PROPIO de cada red (instagram rosa, facebook azul...) es para la
  // página de contacto, donde el ícono ES el contenido. Acá es un adorno
  // DENTRO de la barra del productor: con su color de marca compite con el
  // fondo que el productor eligió y deja de leerse como parte de la barra.
  // Por eso van todos en `colorTexto`, igual que el mensaje.
  const tamanoIcono = variante === 'franja' ? 15 : 18;
  const filaIconos = redesActivas.length > 0 && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {redesActivas.map((r) => (
        <a
          key={r.key}
          href={r.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={r.label}
          style={{ color: colorTexto, fontSize: tamanoIcono, display: 'flex', alignItems: 'center' }}
        >
          {r.icon}
        </a>
      ))}
    </div>
  );

  // EL COLOR DEL BRILLO depende del color del texto, no es fijo: sobre un
  // texto claro un destello blanco no se ve, y sobre uno oscuro un destello
  // oscuro tampoco. Se mira la luminancia del color elegido y se va hacia el
  // lado contrario, siempre con transparencia para que sea un destello y no
  // un cambio de color.
  const brillo = esClaro(colorTexto) ? 'rgba(0,0,0,.32)' : 'rgba(255,255,255,.6)';

  const estiloTexto = {
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
    color: colorTexto,
    fontFamily: tipografia.body,
    // Variables para el CSS del brillo (`.pj-bienvenida-texto`).
    '--pjb-texto': colorTexto,
    '--pjb-brillo': brillo,
    ...(variante === 'franja'
      ? { fontSize: 13, lineHeight: 1.4 }
      : {}),
  };

  // SIN LINK: un `<span>`, no un `<a>` sin `href`. Un link que no lleva a
  // ningún lado es peor que no tenerlo — mismo criterio que los botones
  // configurables del hero (`BotonesConfigurados`, en `CatalogoPage.jsx`).
  const nodoTexto = destino ? (
    <a
      href={destino}
      className="pj-bienvenida-link pj-bienvenida-texto"
      style={{ ...estiloTexto, textDecoration: 'none', display: 'block' }}
      {...(externa ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {mensaje}
    </a>
  ) : (
    <span className="pj-bienvenida-texto" style={{ ...estiloTexto, display: 'block' }}>{mensaje}</span>
  );

  if (variante === 'franja') {
    // Una sola fila: texto e íconos comparten el mismo fondo. Es finita —
    // comparte lugar con el menú— así que si no entra todo en una línea, el
    // `flexWrap` manda los íconos debajo en vez de desbordar.
    // EL FONDO VA DE BORDE A BORDE Y EL CONTENIDO NO: el texto y los íconos
    // viven en un contenedor con el MISMO ancho máximo y el MISMO padding
    // lateral que `inner()` de `SiteHeader` (1400px, 24px), así arrancan
    // alineados con el logo y el menú que tienen justo debajo. Sin esto el
    // texto pegaba contra el borde de la pantalla mientras el resto del sitio
    // respiraba. Es una clase propia y no `site-header-inner` a propósito:
    // el countdown del remate mide ese contenedor con `querySelector`, y una
    // segunda copia en el DOM le devolvería la equivocada.
    return (
      <>
        <style>{BIENVENIDA_CSS}</style>
        <div style={{ background: fondo, padding: '6px 0', fontFamily: tipografia.body }}>
          <div
            className="pj-bienvenida-interior"
            style={{
              maxWidth: 1400,
              margin: '0 auto',
              padding: '0 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            {nodoTexto}
            {filaIconos}
          </div>
        </div>
      </>
    );
  }

  // BANDA (OP-2): dos bloques, como el wireframe — el claro con el mensaje y
  // el oscuro con los íconos. El contenedor entero sigue usando `fondo`; el
  // bloque de íconos le suma un sobreoscurecido sutil encima. Sin redes
  // cargadas el bloque oscuro ni se dibuja: uno vacío sería un rectángulo sin
  // motivo.
  return (
    <>
      <style>{BIENVENIDA_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', background: fondo, fontFamily: tipografia.body }}>
        <div style={{ flex: 1, minWidth: 0, padding: PADDING_POR_VARIANTE.banda, display: 'flex', alignItems: 'center' }}>
          {nodoTexto}
        </div>
        {filaIconos && (
          <div
            className="pj-bienvenida-banda-iconos"
            style={{
              background: 'rgba(0,0,0,.18)',
              padding: '12px 24px',
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {filaIconos}
          </div>
        )}
      </div>
    </>
  );
};

export default BarraBienvenida;
